import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { canAccessFeature } from "../auth/featureScopes";
import { useAuth } from "../contexts/AuthContext";
import useApiHelper from "../hooks/useApiHelper";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import usePricingAccess from "../hooks/usePricingAccess";
import { ILead } from "../models/Lead";
import {
  ICalculateQuoteRequest,
  ICalculatedQuoteResponse,
  ICancelQuoteLeadRequest,
  ICreateLeadFromQuoteRequest,
  IQuoteCalculatorAvailabilityResponse,
} from "../models/QuoteCalculator";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

/**
 * Whether the signed-in user gets the quote calculator at all. Three gates, cheapest first: the
 * customer works leads ("Lead Management", or "Call Centre" whose bookings and quotes sit under
 * the same leads scope), the role can open them and see prices, and the API says this Wilma is
 * connected to its widget (Widget:ApiUrl / Widget:ApiToken in Betty).
 */
export function useQuoteCalculatorAccess(componentName = "useQuoteCalculatorAccess") {
  const { get } = useApiHelper(apiConfig(componentName));
  const { featureScopes } = useAuth();
  const hasLeads = useFeatureFlag("Lead Management");
  const isCallCentre = useFeatureFlag("Call Centre");
  const { canViewPrices } = usePricingAccess();

  const roleAllows = (hasLeads || isCallCentre) && canViewPrices && canAccessFeature(featureScopes, "leads");

  const availability = useQuery({
    queryKey: ["quote-calculator", "enabled"],
    queryFn: () => get<IQuoteCalculatorAvailabilityResponse>("/quote-calculator/enabled"),
    enabled: roleAllows,
    staleTime: 1000 * 60 * 10,
  });

  return {
    availability,
    show: roleAllows && availability.data?.data?.enabled === true,
  };
}

/** Price a vehicle with the widget's pricing engine, then optionally make a lead of the quote. */
export default function useQuoteCalculator(componentName = "useQuoteCalculator") {
  const { post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const calculate = useMutation((body: ICalculateQuoteRequest) =>
    post<ICalculateQuoteRequest, ICalculatedQuoteResponse>("/quote-calculator/calculate", body)
  );

  const createLead = useMutation(
    ({ quoteId, body }: { quoteId: string; body: ICreateLeadFromQuoteRequest }) =>
      post<ICreateLeadFromQuoteRequest, DefaultResponseWithData<ILead>>(`/quote-calculator/${quoteId}/lead`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["pagedTable", "/lead/partialwithfilters"]);
      },
    }
  );

  // The seller declined while the calculator was still open: withdraw what it just made.
  const cancelLead = useMutation(
    ({ leadId, body }: { leadId: string; body: ICancelQuoteLeadRequest }) =>
      post<ICancelQuoteLeadRequest, DefaultResponseWithData<ILead>>(`/quote-calculator/${leadId}/cancel`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["pagedTable", "/lead/partialwithfilters"]);
      },
    }
  );

  return { calculate, createLead, cancelLead };
}
